import React, { useState } from "react";
import { PivotView } from "../types";
import { FeatherX, FeatherEdit2 } from "@subframe/core";
import { Button } from "../../../../../../ui/components/Button";

interface PivotViewTabsProps {
  views: PivotView[];
  activeViewId: string;
  onSelectView: (viewId: string) => void;
  onRenameView: (viewId: string, newName: string) => void;
  onDeleteView: (viewId: string) => void;
  onCreateNew: () => void;
}

export const PivotViewTabs: React.FC<PivotViewTabsProps> = ({
  views,
  activeViewId,
  onSelectView,
  onRenameView,
  onDeleteView,
  onCreateNew,
}) => {
  const [editingViewId, setEditingViewId] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>("");

  const handleStartEdit = (view: PivotView) => {
    setEditingViewId(view.id);
    setEditName(view.name);
  };

  const handleSaveEdit = (viewId: string) => {
    if (editName.trim()) {
      onRenameView(viewId, editName.trim());
    }
    setEditingViewId(null);
    setEditName("");
  };

  const handleCancelEdit = () => {
    setEditingViewId(null);
    setEditName("");
  };

  const handleKeyDown = (e: React.KeyboardEvent, viewId: string) => {
    if (e.key === "Enter") {
      handleSaveEdit(viewId);
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const canDelete = views.length > 1;

  return (
    <div className="flex items-center gap-2 border-b border-neutral-border bg-default-background px-4 py-2">
      <div className="flex items-center gap-1 overflow-x-auto flex-1">
        {views.map((view) => {
          const isActive = view.id === activeViewId;
          const isEditing = editingViewId === view.id;

          return (
            <div
              key={view.id}
              className={`flex items-center gap-1 px-3 py-2 rounded-t-md border-b-2 transition-colors ${
                isActive
                  ? "border-brand-600 bg-brand-50"
                  : "border-transparent hover:bg-neutral-50 cursor-pointer"
              }`}
              onClick={() => !isEditing && onSelectView(view.id)}
            >
              {isEditing ? (
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={() => handleSaveEdit(view.id)}
                  onKeyDown={(e) => handleKeyDown(e, view.id)}
                  className="px-2 py-1 rounded border border-brand-600 bg-default-background text-body font-body text-default-font min-w-[100px]"
                  autoFocus
                />
              ) : (
                <>
                  <span
                    className={`text-body font-body ${
                      isActive ? "text-brand-700 font-body-bold" : "text-default-font"
                    }`}
                  >
                    {view.name}
                  </span>
                  {isActive && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartEdit(view);
                        }}
                        className="text-subtext-color hover:text-default-font transition-colors"
                        title="Rename"
                      >
                        <FeatherEdit2 className="w-3 h-3" />
                      </button>
                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Delete "${view.name}"?`)) {
                              onDeleteView(view.id);
                            }
                          }}
                          className="text-subtext-color hover:text-error-600 transition-colors"
                          title="Delete"
                        >
                          <FeatherX className="w-4 h-4" />
                        </button>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <Button
        variant="neutral-tertiary"
        size="small"
        onClick={onCreateNew}
      >
        + New View
      </Button>
    </div>
  );
};

